import Head from 'next/head';
import TournamentsSidebar from './_components/TournamentsSidebar';
import { NextPageWithLayout } from '../_app.page';
import withOptionalAuth from '@/components/withOptionalAuth';
import MainLayout from '@/layouts/MainLayout';
import FeatureLinkCard from '@/components/FeatureLinkCard';
import cardIconTournament from '@/assets/card_icon_tournament.png';
import TournamentsFaq from './_components/TournamentsFaq';
import { useTournamentStore } from '@/lib/Tournaments/store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthUser } from '@/hooks/useAuthUser';
import { firebaseAuth } from '@/lib/Firebase';
import { useLoginModal } from '@/common/LoginModal';
import Button, { ButtonType } from '@/components/Button';
import Link from 'next/link';
import { useTournaments } from '@/hooks/useTournaments';
import Heading from '@/common/Heading';
import TournamentCard from './_components/TournamentCard';
import { Icon24Add } from '@vkontakte/icons';
import LoadingScreen from '@/common/LoadingScreen';
import Loader from '@/components/Loader';

const INITIAL_PAST_TOURNAMENTS = 2;
const PAST_TOURNAMENTS_STEP = 2;
const PAST_TOURNAMENTS_INLINE_LIMIT = 6;

function TounamentsHomePage() {
  const { data: authUser,isLoading: isAuthLoading } = useAuthUser(['authUser'], firebaseAuth);
  const { requireAuth } = useLoginModal();
  const { tournaments } = useTournaments();
  const [visiblePastCount, setVisiblePastCount] = useState(INITIAL_PAST_TOURNAMENTS);
  const [isLoading, setIsLoading] = useState(true);

useEffect(() => {
  if (isAuthLoading) {
    return;
  }
  const timeout = setTimeout(() => {
    setIsLoading(false);
  }, 1000);
  return () => clearTimeout(timeout);
}, [isAuthLoading]);

  const handleEnterTournament = async () => {
    if (!authUser) {
      await requireAuth();
    }
  };

  const scrollToFaq = useCallback(() => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const { setTournamentId } = useTournamentStore();
  useEffect(() => {
    setTournamentId('');
  }, [setTournamentId]);

  const pastTournaments = tournaments?.pastTournaments ?? [];
  const visiblePastTournaments = pastTournaments.slice(
    0,
    Math.min(visiblePastCount, PAST_TOURNAMENTS_INLINE_LIMIT)
  );
  const canLoadMorePastInline =
    visiblePastCount < Math.min(pastTournaments.length, PAST_TOURNAMENTS_INLINE_LIMIT);
  const shouldShowSeeMorePast =
    pastTournaments.length > PAST_TOURNAMENTS_INLINE_LIMIT &&
    visiblePastCount >= PAST_TOURNAMENTS_INLINE_LIMIT;

  const handleLoadMorePast = () => {
    setVisiblePastCount((currentCount) =>
      Math.min(currentCount + PAST_TOURNAMENTS_STEP, PAST_TOURNAMENTS_INLINE_LIMIT)
    );
  };

  if (isLoading) {
      <Loader className="size-10 mx-auto my-8" />    
  }

  return (
    <div className="py-6">
      <div className="flex flex-col max-w-screen-lg lg:mx-auto">
        <Head>
          <title>Tournaments | Bloom</title>
        </Head>

        <FeatureLinkCard
          onClick={!!authUser ? scrollToFaq : handleEnterTournament}
          title="🏆 Compete in Tournaments"
          description="Test your investing skills against other players in real-time stock picking competitions."
          cta={!!authUser ? 'How it works' : 'Enter tournament'}
          iconSrc={cardIconTournament}
          animateOnMount
          horizontalDesktop={!!authUser?.uid}
        />


        {authUser ? (
          <div className="mt-6 gap-6 md:grid grid-cols-2 lg:grid-cols-5">
            <TournamentsSidebar sections={['upcoming']} className="col-span-1 lg:col-span-3" />

            <TournamentsSidebar
              sections={['live']}
              className="col-span-1 lg:col-span-2 md:row-span-3"
            />

            {!!pastTournaments.length && (
              <div className="lg:scrollbar-apple flex flex-col gap-6 col-span-1 lg:col-span-3">
                <div className="flex flex-col gap-1">
                  <Heading as="h4">
                    <div className="flex items-center gap-2">
                      Past tournaments
                      <span className="bg-secondary rounded-full size-2" />
                    </div>
                  </Heading>
                  <div className="flex flex-col gap-4">
                    {visiblePastTournaments.map((tournament) => (
                      <TournamentCard key={tournament.id} tournament={tournament} />
                    ))}
                  </div>
                </div>

                {canLoadMorePastInline && (
                  <div className="flex justify-center">
                    <button
                      type="button"
                      aria-label="Load more past tournaments"
                      onClick={handleLoadMorePast}
                      className="size-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center"
                    >
                      <Icon24Add className="text-secondary" />
                    </button>
                  </div>
                )}

                {shouldShowSeeMorePast && (
                  <Link href="/tournaments/past" className="block">
                    <Button type={ButtonType.Secondary} size="md" className="w-full">
                      See more past tournaments
                    </Button>
                  </Link>
                )}
              </div>
            )}
            <div className="col-span-2 lg:col-span-3">
              <TournamentsFaq />
            </div>
          </div>
        ) : (
          <div>
            <TournamentsFaq />
          </div>
        )}
      </div>
    </div>
  );
}

const TournamentsOptionalAuth = withOptionalAuth(TounamentsHomePage) as NextPageWithLayout;

export default TournamentsOptionalAuth;

TournamentsOptionalAuth.getLayout = MainLayout;
